import cn from 'classnames';
import { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { trackEvent } from '@/shared/lib';
import { Button, Tooltip, WidgetCard } from '@/shared/ui';

import { ModalID, modalModel } from '@/entities/modal';
import { patientModel } from '@/entities/patient';

import { type StructuredReportsAndStudiesType } from '@/features/reportCards';

import { ClinicalCaseDescription } from '@/widgets/PatientInfo';

import { useReportPreviewSlides } from '../../hooks';
import { getCompareImages } from '../../lib';
import { ReportPreviewSlideshow } from '../ReportPreviewSlideshow/ReportPreviewSlideshow';

import styles from './PatientSummaryHero.module.scss';

type PatientSummaryHeroProps = {
  patientID: string;
  reports: StructuredReportsAndStudiesType[];
  className?: string;
};

export const PatientSummaryHero: FC<PatientSummaryHeroProps> = (props) => {
  const { patientID, reports, className } = props;

  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const { CanViewClinicalCaseDescription } = useAppSelector(
    patientModel.selectors.selectPatientYourPermissions(patientID),
  );
  const slides = useReportPreviewSlides(reports);

  const hasSlides = slides.length > 0;
  const compareImages = getCompareImages(slides);

  if (!CanViewClinicalCaseDescription && !hasSlides) {
    return null;
  }

  const openCompare = () => {
    if (!compareImages) {
      return;
    }

    trackEvent({
      eventName: 'PatientProfilePageCompareStudiesClicked',
      properties: { patient_id: patientID },
    });

    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.StudyCompare,
        data: compareImages,
      }),
    );
  };

  const compareButton = (
    <Button
      variant="primary"
      size="small"
      icon="split"
      disabled={!compareImages}
      onClick={openCompare}
    >
      <FormattedMessage
        id="patientProfile.compare.button"
        defaultMessage="Compare"
      />
    </Button>
  );

  return (
    <WidgetCard
      className={cn(
        styles.container,
        hasSlides && styles.withSlides,
        className,
      )}
      data-section="patient-summary"
    >
      {CanViewClinicalCaseDescription && (
        <ClinicalCaseDescription
          patientID={patientID}
          layout="plain"
          className={styles.summary}
        />
      )}

      {hasSlides && (
        <ReportPreviewSlideshow
          key={patientID}
          slides={slides}
          className={styles.slideshow}
          actions={
            compareImages ? (
              compareButton
            ) : (
              <Tooltip.Primary
                content={formatMessage({
                  id: 'patientProfile.compare.unavailable',
                  defaultMessage: 'Needs at least two studies',
                })}
              >
                <span>{compareButton}</span>
              </Tooltip.Primary>
            )
          }
        />
      )}
    </WidgetCard>
  );
};
